/**
 * Session Cache Utility (Aug 2026 — httpOnly cookie migration)
 *
 * Tokens now live exclusively in httpOnly cookies set by the backend
 * (invisible to JS, so an XSS payload can't read them). This module only
 * caches non-sensitive PROFILE data (id/name/email/role) for instant UI
 * hydration on page load — it never stores tokens.
 */

// ==================== ADMIN CACHE (sessionStorage - auto-clears on tab close) ====================

export const AdminStorage = {
  /**
   * Cache the logged-in admin's profile after a successful login/MFA/refresh.
   */
  setAdmin: (admin, role) => {
    sessionStorage.setItem('admin', JSON.stringify(admin));
    sessionStorage.setItem('adminRole', role || admin?.role || 'staff');
  },

  getAdmin: () => {
    const adminStr = sessionStorage.getItem('admin');
    return adminStr ? JSON.parse(adminStr) : null;
  },

  getRole: () => {
    return sessionStorage.getItem('adminRole') || 'staff';
  },

  /**
   * Best-effort "is this tab holding an admin session" check. The actual
   * source of truth is the httpOnly admin_access_token cookie — this only
   * reflects the cached profile written at login time.
   */
  isLoggedIn: () => {
    return !!sessionStorage.getItem('admin');
  },

  clear: () => {
    sessionStorage.removeItem('admin');
    sessionStorage.removeItem('adminRole');
  }
};

// ==================== CUSTOMER CACHE (localStorage - persists across sessions) ====================

export const CustomerStorage = {
  setUser: (user) => {
    localStorage.setItem('user', JSON.stringify(user));
  },

  getUser: () => {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  },

  setGuest: (guest) => {
    localStorage.setItem('guest', JSON.stringify(guest));
  },

  getGuest: () => {
    const guestStr = localStorage.getItem('guest');
    return guestStr ? JSON.parse(guestStr) : null;
  },

  /**
   * Best-effort "is this browser holding a customer session" check. The
   * actual source of truth is the httpOnly access_token cookie — this
   * only reflects the cached profile written at login time.
   */
  isLoggedIn: () => {
    return !!(localStorage.getItem('user') || localStorage.getItem('guest'));
  },

  /**
   * Clear customer session AND any in-flight UX state.
   * Use on explicit logout, or on any session-ending event where the next
   * user of the device should start fresh (kiosks, shared devices).
   */
  clear: () => {
    localStorage.removeItem('user');
    localStorage.removeItem('guest');
    localStorage.removeItem('bookingIntent');
    localStorage.removeItem('smashzone_selection');
    // Reset the "Session extended" toast cooldown + last-login marker on
    // logout so the next user of this device (a) doesn't inherit the
    // previous user's 24h suppression, and (b) doesn't get the toast on
    // their very first auto-refresh right after a brand-new login.
    localStorage.removeItem('lastSessionExtendedToastAt');
    localStorage.removeItem('lastLoginAt');
    // Deliberately KEEP `lastEmailRemember` so the user's opt-out
    // preference persists across logins.
    localStorage.removeItem('lastEmail');
    try {
      sessionStorage.removeItem('smashzone_flow_snapshot');
    } catch (e) {
      console.warn('Could not clear flow snapshot from sessionStorage:', e);
    }
  },

  /**
   * Clear ONLY authentication-related keys; preserve bookingIntent and
   * smashzone_selection so the post-login "resume booking" flow still works.
   */
  clearAuthOnly: () => {
    localStorage.removeItem('user');
    localStorage.removeItem('guest');
  }
};

// ==================== GLOBAL STORAGE MANAGEMENT ====================

/**
 * Clear ALL cached profiles (admin + customer)
 * CRITICAL: Use this when switching between admin and customer contexts
 */
export const clearAllStorage = () => {
  AdminStorage.clear();
  CustomerStorage.clear();
};

/**
 * SECURITY: Detect and clean mixed sessions
 * If an admin profile is cached when a customer logs in (or vice versa),
 * clear it.
 */
export const ensureCleanSession = (context = 'customer') => {
  if (context === 'customer' && AdminStorage.isLoggedIn()) {
    console.warn('⚠️ Admin session detected during customer login - clearing admin session');
    AdminStorage.clear();
  } else if (context === 'admin' && CustomerStorage.isLoggedIn()) {
    console.warn('⚠️ Customer session detected during admin login - clearing customer session');
    CustomerStorage.clear();
  }
};

export default {
  AdminStorage,
  CustomerStorage,
  clearAllStorage,
  ensureCleanSession
};
